import { invariant } from '@seen/core/invariant'
import { ParticipantLabel } from '@seen/core/models/participantLabel'
import { ParticipantView } from '@seen/core/models/participantView'
import * as patients from '@seen/core/models/patients'
import { Participant } from '@seen/core/models/patients/participant'
import {
  ATTENDANCE_WEEKDAYS,
  type AttendanceWeekday,
  DayCenterSchedule,
} from '@seen/core/models/schedules/dayCenterSchedule'
import { getSearchKey, normalizeQuery } from '@seen/core/search'
import { recordValues } from '@seen/core/utils'
import { Button, cn, IconUser } from '@seen/ui'
import { IconCalendarWeek, IconFlag } from '@tabler/icons-react'
import { useQueryClient } from '@tanstack/react-query'
import { isEqual, orderBy } from 'lodash-es'
import pluralize from 'pluralize'
import { type ReactNode, useCallback, useMemo, useState } from 'react'
import { match } from 'ts-pattern'
import { Avatar } from '#src/components/core/Avatar/Avatar.tsx'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { ScrollArea } from '#src/components/core/scroll-area/ScrollArea.tsx'
import { VirtualizedList } from '#src/components/core/VirtualizedList.tsx'
import type {
  Filter,
  FilterConfigMap,
  FilterOption,
} from '#src/components/filter/Filter.tsx'
import { FilterEditor } from '#src/components/filter/FilterEditor.tsx'
import { useFilteredData } from '#src/components/filter/useFilteredData.ts'
import { Helmet } from '#src/components/Helmet.tsx'
import { IconLabel } from '#src/components/icons/IconLabel.tsx'
import { ListHeader } from '#src/components/ListHeader.tsx'
import { ParticipantOption } from '#src/components/options/ParticipantOption.tsx'
import { WEEKDAY_LONG_LABELS } from '#src/components/patient/detail-content/day-center-schedule/weekdays.ts'
import { useLabelFilterOptions } from '#src/components/UseLabelFilterOptions.tsx'
import { ViewEditorRow } from '#src/components/ViewEditorRow.tsx'
import { ViewRow } from '#src/components/ViewRow.tsx'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { usePortalStore } from '#src/hooks/usePortalStore.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { useSearch } from '#src/hooks/useSearch.ts'
import { DailyAttendance } from '#src/pages/participants/DailyAttendance.tsx'
import { DayCenterAttendance } from '#src/pages/participants/DayCenterAttendance.tsx'
import { DisplayEditor } from '#src/pages/participants/DisplayEditor.tsx'
import { ParticipantRow } from '#src/pages/participants/ParticipantRow.tsx'
import { ParticipantViewComposerDialogContent } from '#src/pages/participants/ParticipantViewComposerDialogContent.tsx'
import {
  DEFAULT_DISPLAY,
  DEFAULT_FILTERS,
  type ParticipantPageView,
  type ParticipantViewDisplay,
  useParticipantsPageStore,
} from '#src/pages/participants/useParticipantsPageStore.ts'
import { readOptions } from '#src/query-options/readOptions.ts'

export function ParticipantsPage(): ReactNode {
  const selectedView = useParticipantsPageStore((state) => state.view)
  const setSelectedView = useParticipantsPageStore((state) => state.setView)

  return (
    <div className={cn('relative flex flex-col', 'size-full print:size-auto')}>
      <Helmet title="Participants" />
      <ParticipantViewRow
        selectedView={selectedView}
        onSelectView={setSelectedView}
      />
      <div className="flex min-h-0 flex-1 flex-col">
        {selectedView !== 'daily-attendance' &&
          selectedView !== 'day-center-am' &&
          selectedView !== 'day-center-pm' && (
            <ParticipantsView view={selectedView} />
          )}
        {selectedView === 'daily-attendance' && <DailyAttendanceView />}
        {selectedView === 'day-center-am' && (
          <DayCenterAttendanceView period="am" />
        )}
        {selectedView === 'day-center-pm' && (
          <DayCenterAttendanceView period="pm" />
        )}
      </div>
    </div>
  )
}

function ParticipantViewRow({
  selectedView,
  onSelectView,
}: {
  selectedView: ParticipantPageView
  onSelectView: (view: ParticipantPageView) => void
}): ReactNode {
  const currentPractitioner = useCurrentPractitioner()
  const { data: views } = useSearch(
    ParticipantView,
    ParticipantView.queryForPractitioner(currentPractitioner.reference)
  )
  return (
    <ListHeader>
      <Button
        onClick={() => onSelectView('all')}
        variant="outline"
        size="xs"
        selected={selectedView === 'all'}
      >
        All participants
      </Button>
      <Button
        onClick={() => onSelectView('daily-attendance')}
        variant="outline"
        size="xs"
        selected={selectedView === 'daily-attendance'}
      >
        Daily attendance
      </Button>
      <Button
        onClick={() => onSelectView('day-center-am')}
        variant="outline"
        size="xs"
        selected={selectedView === 'day-center-am'}
      >
        Day Center - AM
      </Button>
      <Button
        onClick={() => onSelectView('day-center-pm')}
        variant="outline"
        size="xs"
        selected={selectedView === 'day-center-pm'}
      >
        Day Center - PM
      </Button>
      <ViewRow
        views={views}
        selectedView={selectedView}
        onSelectView={onSelectView}
        onDeleteSelectedView={() => onSelectView('all')}
        className="flex-1"
      />
    </ListHeader>
  )
}

function DailyAttendanceView(): ReactNode {
  return (
    <ScrollArea scrollRestorationId="daily-attendance">
      <div className="p-pagex-default lg:p-pagex-lg">
        <DailyAttendance />
      </div>
    </ScrollArea>
  )
}

function DayCenterAttendanceView({
  period,
}: {
  period: 'am' | 'pm'
}): ReactNode {
  return (
    <ScrollArea scrollRestorationId={`day-center-attendance-${period}`}>
      <div className="p-pagex-default lg:p-pagex-lg">
        <DayCenterAttendance period={period} />
      </div>
    </ScrollArea>
  )
}

const ATTENDANCE_DAY_FILTER_OPTIONS: FilterOption[] = ATTENDANCE_WEEKDAYS.map(
  (day) => ({
    value: day,
    label: WEEKDAY_LONG_LABELS[day],
    keywords: [WEEKDAY_LONG_LABELS[day].toLowerCase(), day],
  })
)

function ParticipantsView({
  view,
}: {
  view: ParticipantView['referenceString'] | 'all'
}): ReactNode {
  const participants = useParticipantsPageParticipants()

  const participantOptions = useMemo(
    () =>
      participants.map((p) => ({
        label: <ParticipantOption participant={p.reference} />,
        value: p.reference.reference,
        icon: <Avatar profile={p.reference} size={3.5} />,
        keywords: p.searchKeywords,
      })),
    [participants]
  )

  const { data: labels } = useSearch(
    ParticipantLabel,
    ParticipantLabel.baseSearchQuery()
  )
  const labelOptions = useLabelFilterOptions(labels)

  const filters = useFilters(view)

  // Day-center attendance lives on a separate Schedule resource, so load it only
  // when the attendance-day filter is active and index the days by participant.
  const { data: schedules } = useSearch(
    DayCenterSchedule,
    filters.some((filter) => filter.field === 'attendanceDay')
      ? DayCenterSchedule.baseSearchQuery()
      : undefined
  )
  const attendanceDaysByParticipant = useMemo(() => {
    const map = new Map<string, AttendanceWeekday[]>()
    for (const schedule of schedules ?? []) {
      const reference = schedule.participant?.reference
      if (reference) {
        map.set(reference, schedule.attendanceDays)
      }
    }
    return map
  }, [schedules])

  const filterOptions = useMemo<FilterConfigMap<Participant>>(
    () => ({
      labels: {
        label: 'Labels',
        icon: <IconLabel />,
        type: 'multi-select-array',
        options: labelOptions,
        valueFn: (p) => p.labels,
      },
      flags: {
        label: 'Flags',
        icon: <IconFlag className="size-4" />,
        type: 'multi-select-array',
        options: flagFilterOptions,
        valueFn: (p) => p.flagCodes,
      },
      attendanceDay: {
        label: 'Attendance day',
        icon: <IconCalendarWeek className="size-4" />,
        type: 'multi-select-array',
        // Default to "include any of" (OR) — a participant attending any
        // selected day matches, rather than requiring all selected days.
        defaultOperator: 'contains',
        options: ATTENDANCE_DAY_FILTER_OPTIONS,
        valueFn: (p) =>
          attendanceDaysByParticipant.get(p.reference.reference) ?? [],
      },
      participant: {
        label: 'Participant',
        icon: <IconUser className="size-4" />,
        type: 'multi-select',
        options: participantOptions,
        valueFn: (p) => p.reference.reference,
      },
    }),
    [participantOptions, labelOptions, attendanceDaysByParticipant]
  )

  const filteredParticipants = useFilteredData(
    participants,
    filterOptions,
    filters
  )

  const display = useDisplay(view)

  const rows = useMemo(() => {
    const resultParticipants = match(display.ordering)
      .with('name', () =>
        orderBy(filteredParticipants, (p) => p.name.toLowerCase(), 'asc')
      )
      .with('enrollment-date', () =>
        orderBy(
          filteredParticipants,
          (p) => p.enrollmentDateString ?? '',
          'desc'
        )
      )
      .exhaustive()
    return resultParticipants.map((participant) => (
      <ParticipantRow
        key={participant.id}
        participant={participant}
        display={display}
      />
    ))
  }, [filteredParticipants, display])

  return (
    <div className="flex size-full flex-col">
      <FilterRow
        selectedView={view}
        filterOptions={filterOptions}
        rowCount={rows.length}
      />
      <div className="flex min-h-0 flex-1">
        <VirtualizedList
          rows={rows}
          estimateSize={getParticipantRowHeight}
          scrollRestorationId="participants-page"
        />
      </div>
    </div>
  )
}

function FilterRow({
  selectedView,
  filterOptions,
  rowCount,
}: {
  selectedView: ParticipantView['referenceString'] | 'all'
  filterOptions: FilterConfigMap<Participant>
  rowCount: number
}): ReactNode {
  const filters = useFilters(selectedView)
  const baseFilters = useBaseFilters(selectedView)
  const isFiltersDirty = useMemo(
    () => !isEqual(filters, baseFilters ?? DEFAULT_FILTERS),
    [baseFilters, filters]
  )

  const display = useDisplay(selectedView)
  const baseDisplay = useBaseDisplay(selectedView)
  const isDisplayDirty = useMemo(
    () =>
      !isEqual(display, {
        ...DEFAULT_DISPLAY,
        ...baseDisplay,
      }),
    [baseDisplay, display]
  )

  const setFiltersOverride = useParticipantsPageStore(
    (state) => state.setFilters
  )
  const handleSetFilters = useCallback(
    (f: Filter[] | ((currentFilters: Filter[]) => Filter[])) => {
      const newF =
        typeof f === 'function'
          ? (currFilters: Filter[] | undefined) => f(currFilters ?? filters)
          : f
      setFiltersOverride({
        filters: newF,
        forView: selectedView,
      })
    },
    [setFiltersOverride, selectedView, filters]
  )

  const unsetFiltersOverride = useParticipantsPageStore(
    (state) => state.unsetFilters
  )

  const displayOverride = useParticipantsPageStore(
    (state) => state.displayByView[selectedView]
  )

  const unsetDisplayOverride = useParticipantsPageStore(
    (state) => state.unsetDisplay
  )

  const [isComposerOpen, setIsComposerOpen] = useState(false)

  const setDisplayOverride = useParticipantsPageStore(
    (state) => state.setDisplay
  )
  const setDisplayOverrideForSelectedView = useCallback(
    (
      display:
        | Partial<ParticipantViewDisplay>
        | ((
            currentDisplay: Partial<ParticipantViewDisplay>
          ) => Partial<ParticipantViewDisplay>)
    ) => {
      setDisplayOverride({
        display,
        forView: selectedView,
      })
    },
    [setDisplayOverride, selectedView]
  )

  const updateView = useUpdateView()

  return (
    <>
      <ViewEditorRow
        filterEditor={
          <div className="flex items-center gap-2">
            <FilterEditor
              filterOptions={filterOptions}
              filters={filters}
              setFilters={handleSetFilters}
            />
            <div className="text-2xs whitespace-nowrap text-foreground-secondary">
              {rowCount} {pluralize('participant', rowCount)}
            </div>
          </div>
        }
        displayEditor={
          <DisplayEditor
            value={display}
            onChange={setDisplayOverrideForSelectedView}
            isDirty={isDisplayDirty}
            onReset={() => {
              unsetDisplayOverride(selectedView)
            }}
          />
        }
        isDirty={isFiltersDirty || isDisplayDirty}
        onCreate={() => setIsComposerOpen(true)}
        editable={selectedView !== 'all'}
        onUpdate={() => {
          invariant(selectedView !== 'all', 'Cannot save all view')
          void updateView(selectedView, {
            filters,
            display: displayOverride,
          })
        }}
        onReset={() => {
          unsetDisplayOverride(selectedView)
          unsetFiltersOverride(selectedView)
        }}
      />
      <Dialog open={isComposerOpen} onOpenChange={setIsComposerOpen}>
        <DialogDisplay>
          <ParticipantViewComposerDialogContent
            filters={filters}
            display={displayOverride}
            onCreate={() => {
              setIsComposerOpen(false)
            }}
          />
        </DialogDisplay>
      </Dialog>
    </>
  )
}

function useBaseFilters(
  view: ParticipantView['referenceString'] | 'all'
): Filter[] | undefined {
  const { data: participantView } = useRead(
    ParticipantView,
    view === 'all' ? undefined : view,
    { query: { enabled: view !== 'all' } }
  )

  return useMemo(() => {
    if (!participantView) {
      return undefined
    }
    return JSON.parse(participantView.filters) as Filter[]
  }, [participantView])
}

function useFilters(
  view: ParticipantView['referenceString'] | 'all'
): Filter[] {
  const baseFilters = useBaseFilters(view)

  const filtersOverride = useParticipantsPageStore(
    (state) => state.filtersByView[view]
  )

  return filtersOverride ?? baseFilters ?? DEFAULT_FILTERS
}

function useBaseDisplay(
  view: ParticipantView['referenceString'] | 'all'
): ParticipantViewDisplay {
  const { data: participantView } = useRead(
    ParticipantView,
    view === 'all' ? undefined : view,
    { query: { enabled: view !== 'all' } }
  )

  return useMemo(
    () =>
      // lint-reference-display-ignore -- a stored view's JSON settings
      JSON.parse(participantView?.display ?? '{}') as ParticipantViewDisplay,
    [participantView]
  )
}

function useDisplay(
  view: ParticipantView['referenceString'] | 'all'
): ParticipantViewDisplay {
  const baseDisplay = useBaseDisplay(view)

  const displayOverride = useParticipantsPageStore(
    (state) => state.displayByView[view]
  )

  return useMemo(
    () => ({
      ...DEFAULT_DISPLAY,
      ...baseDisplay,
      ...displayOverride,
    }),
    [baseDisplay, displayOverride]
  )
}

function getParticipantRowHeight(): number {
  return 44
}

function useParticipantsPageParticipants(): Participant[] {
  const showDemoPatientsOnly = usePortalStore(
    (state) => state.showDemoPatientsOnly
  )

  // We try to use the cached participants first, but if they're not available, we fetch a smaller subset from the server
  const medplum = useMedplum()
  const queryClient = useQueryClient()
  const searchKey = getSearchKey(
    medplum,
    Participant.RESOURCE_TYPE,
    normalizeQuery(Participant.baseSearchQuery())
  )
  const maybeCachedParticipants = queryClient.getQueryData<Participant[]>(
    cacheKeys.searchResourcePages(Participant.RESOURCE_TYPE, searchKey)
  )
  const { data: participantSubset } = useSearch(
    Participant,
    showDemoPatientsOnly
      ? Participant.demoOnlySearchQuery()
      : Participant.enrolledSearchQuery(),
    { query: { enabled: !maybeCachedParticipants } }
  )
  const participants =
    maybeCachedParticipants ?? participantSubset ?? EMPTY_PARTICIPANTS

  const participantsPageParticipants = useMemo(
    () =>
      participants.filter(
        (participant) =>
          participant.isActive && (!showDemoPatientsOnly || participant.isDemo)
      ),
    [participants, showDemoPatientsOnly]
  )
  return participantsPageParticipants
}

const EMPTY_PARTICIPANTS: Participant[] = []

function useUpdateView(): (
  view: ParticipantView['referenceString'],
  {
    filters,
    display,
  }: {
    filters: Filter[]
    display: Partial<ParticipantViewDisplay> | undefined
  }
) => Promise<void> {
  const mutate = usePatchMutation<ParticipantView>().mutate
  const queryClient = useQueryClient()
  const medplum = useMedplum()
  return useCallback(
    async (view, { filters, display }) => {
      const instance = await queryClient.fetchQuery(
        readOptions(medplum, ParticipantView, view)
      )
      mutate({
        instance,
        mutateFn: (v) => {
          v.filters = JSON.stringify(filters)
          v.display = JSON.stringify(display)
        },
      })
    },
    [mutate, queryClient, medplum]
  )
}

const flagFilterOptions = recordValues(patients.PARTICIPANT_FLAGS).map(
  ({ coding }) => ({
    label: coding.display,
    value: coding.code as patients.ParticipantFlagCode,
    icon: <IconFlag className="size-4" />,
    keywords: [coding.display],
  })
)
